/**
 * §15's two legal clocks, drawn — and the three states the appeal window can honestly be in.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERY NUMBER ON THIS SCREEN COMES FROM `@hockeytd/shared`
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `deriveAppealWindow`, `describeAppealWindow`, `appealMinutesRemaining`,
 * `isAppealWindowClosed` and `deriveLateReportWindow`. Nothing here adds minutes to an
 * instant. The reason is the one `hearing-clocks.ts` gives in its own header: a deadline
 * derived in two places is a deadline that can disagree with itself, and the one place it may
 * live is beside the correctability rule that lets a service instant move.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE THREE BASES ARE THREE DIFFERENT FACTS AND ARE DRAWN AS THREE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   · `not_served` — nothing has been served, so nothing is running. **No countdown appears**,
 *     and none is computed from the issue date. That specific fake is what this module was
 *     refused for once already.
 *   · `not_configured` — service IS recorded, so a window IS running, and nobody can say when
 *     it ends because the tournament has none set. That is the alarming one, and it is drawn
 *     as such, naming Configuration §05 as the remedy.
 *   · `served` — the live countdown, ticking, and showing a closed window as CLOSED rather
 *     than frozen at zero.
 *
 * There is a fourth state which is not a basis: the appeal-window SETTING could not be read at
 * all. That is not the same as "none is configured" and is not rendered as it —
 * {@link APPEAL_SETTINGS_UNREAD} says which it is, and no clock is drawn either way.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHICH DOCUMENT'S SERVICE REACHES THIS COMPONENT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Batch 3's, and no other. §12: *"The appeal period runs from service of this decision."* The
 * Hearing Letter's service is recorded and starts nothing, so the panel never renders this
 * component against batch 1 — the caller's decision, exactly as `deriveAppealWindow`'s own
 * documentation says it must be.
 */

import { useEffect, useState } from "react";

import {
  appealMinutesRemaining,
  deriveAppealWindow,
  deriveLateReportWindow,
  describeAppealWindow,
  isAppealWindowClosed,
} from "@hockeytd/shared";

import type { HearingClockSettings } from "../../api/types.js";
import {
  APPEAL_AUTHORITY,
  APPEAL_BASIS_REMEDY,
  APPEAL_HEADING,
  APPEAL_SETTINGS_UNREAD,
  LATE_REPORT_HEADING,
  appealCountdownText,
  appealPillClass,
  appealPillText,
  lateReportChipText,
  lateReportPillClass,
  serviceInstantText,
} from "./serviceText.js";

/**
 * A clock that ticks.
 *
 * Thirty seconds, not one: the shared derivation reports WHOLE minutes, so a one-second
 * interval would re-render sixty times to change a number once. Half a minute guarantees the
 * displayed minute is never more than thirty seconds stale, which is the most a minute-grained
 * countdown can honestly promise.
 */
function useTick(active: boolean): Date {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => {
      setNow(new Date());
    }, 30_000);
    return () => {
      clearInterval(timer);
    };
  }, [active]);
  return now;
}

export interface AppealClockProps {
  /** **Batch 3's** service instant, and no other document's. Null when nothing is served. */
  servedAt: string | null;
  /** Configuration §05's settings, or null when they could not be read — a different fact. */
  clocks: HearingClockSettings | null;
  timezone: string;
}

export function AppealClock({ servedAt, clocks, timezone }: AppealClockProps): React.JSX.Element {
  // The interval runs only where there is something to count, so a case with nothing served
  // does not wake the page up twice a minute for a sentence that cannot change.
  const live = clocks !== null && clocks.appealWindowMinutes !== null && servedAt !== null;
  const now = useTick(live);

  if (clocks === null) {
    return (
      <div className="apclock apclock--unknown" data-testid="appealClock" data-basis="unread">
        <div className="apclock__head">
          <span className="apclock__title">{APPEAL_HEADING}</span>
          <span className="statuspill statuspill--muted" data-testid="appealPill">
            <span className="sdot" aria-hidden="true" />
            Not Read
          </span>
        </div>
        <span className="apclock__note" data-testid="appealNote">
          {APPEAL_SETTINGS_UNREAD}
        </span>
      </div>
    );
  }

  const window = deriveAppealWindow(servedAt, clocks.appealWindowMinutes);
  const closed = isAppealWindowClosed(window, now);
  const remaining = appealMinutesRemaining(window, now);

  return (
    <div
      className={`apclock apclock--${window.basis}`}
      data-testid="appealClock"
      data-basis={window.basis}
    >
      <div className="apclock__head">
        <span className="apclock__title">{APPEAL_HEADING}</span>
        <span className={appealPillClass(window, closed)} data-testid="appealPill">
          <span className="sdot" aria-hidden="true" />
          {appealPillText(window, closed)}
        </span>
      </div>

      {/* The shared sentence, which says what it counts FROM so the TD can check it against
          the signed paper. It is printed verbatim and is never paraphrased here. */}
      <span className="apclock__sentence" data-testid="appealSentence">
        {describeAppealWindow(window, timezone)}
      </span>

      {window.basis === "served" && remaining !== null ? (
        <>
          <span className="apclock__count" role="status" data-testid="appealCountdown">
            {appealCountdownText(remaining, closed)}
          </span>
          {/* `describeAppealWindow` formats to the DAY, which is enough for a window measured
              in days and not enough for one measured in minutes. The two instants are
              therefore also given to the minute, in the tournament's own zone. */}
          <span className="apclock__exact" data-testid="appealExact">
            Served {serviceInstantText(window.servedAt ?? "", timezone)} · closes{" "}
            {serviceInstantText(window.deadline ?? "", timezone)} ·{" "}
            {String(window.windowMinutes ?? 0)}-minute window
          </span>
        </>
      ) : null}

      <span className="apclock__note" data-testid="appealNote">
        {APPEAL_BASIS_REMEDY[window.basis]}
      </span>
      <span className="apclock__authority">{APPEAL_AUTHORITY}</span>
    </div>
  );
}

export interface LateReportChipProps {
  incidentAt: string;
  /** Null while no report has arrived — then, and only then, the chip counts down. */
  reportReceivedAt: string | null;
  /** Configuration §05's threshold, or null when it could not be read. */
  thresholdHours: number | null;
}

/**
 * §15's first clock — *"a countdown chip per case from the incident time"*.
 *
 * Small and factual, and deliberately not a second late-report banner: §8's banner already
 * states the discretion and its reasons, and this chip states the window that discretion is
 * measured against. Where the threshold could not be read, the chip is absent rather than
 * drawn against an assumed 24 — the number is a Configuration item precisely because a
 * tournament may run a different one.
 */
export function LateReportChip({
  incidentAt,
  reportReceivedAt,
  thresholdHours,
}: LateReportChipProps): React.JSX.Element | null {
  const live = thresholdHours !== null && reportReceivedAt === null;
  const now = useTick(live);
  if (thresholdHours === null) return null;

  const window = deriveLateReportWindow(incidentAt, reportReceivedAt, thresholdHours, now);

  return (
    <span className="lrchip" data-testid="lateReportChip" data-late={String(window.isLate)}>
      <span className="lrchip__title">{LATE_REPORT_HEADING}</span>
      <span className={lateReportPillClass(window.isLate)}>
        <span className="sdot" aria-hidden="true" />
        {window.isLate ? "Late" : "In Time"}
      </span>
      <span className="lrchip__text" data-testid="lateReportChipText">
        {lateReportChipText(window, reportReceivedAt !== null)}
      </span>
    </span>
  );
}
